import React, { useCallback, useEffect, useRef, useState } from "react"
import Card from "../../../../components/atoms/Card.atom"
import FormProjectModules from "./formSections/FormProjectModules.controller"
import FormProjectDetails from "./formSections/FormProjectDetails.controller"
import Divider from "../../../../components/atoms/Divider.atom"
import { useLocation, useNavigate } from "react-router-dom"
import {
  FORM_CLIENT_MODE,
  SISU_CLIENTS,
} from "../../../../consts/modules/clients.consts"
import {
  isNotEmpty,
  isNotMaxLength,
  validateFieldValue,
  fieldsHaveErrors,
  checkAllFields,
} from "../../../../utils/validations.utils"
import FormSectionTitle from "../components/FormSectionTitle"
import FormProjectFooter from "./formSections/FormProjectFooter.controller"
import { getProjectData } from "../../../../services/project.service"
import { getAllProjectModules } from "../../../../services/module.service"
import { getClient } from "../../../../services/client.service"
import useToast from "../../../../hooks/useToast.hook"
import { AppRoutes } from "../../../../consts/appRoutes.consts"
import { useNavigationBlock } from "../../../../contexts/NavigationBlock.context"
import { areObjectsEqual } from "../../../../utils/object.utils"
import ModalConfirmation from "../../../../components/molecules/ModalConfirmation.molecule"
import { clsx } from "../../../../utils/strings.utils"
import _ from "lodash"
import useShowConfirmationTextOnPopState from "../../../../hooks/useShowConfirmationTextOnPopState"

const isFirefox = navigator.userAgent.includes("Firefox")

function setInitialModuleOrder(projectModules) {
  let orderedModules = []
  const modules = Object.groupBy(projectModules, (o) =>
    o.active === 1 ? "active" : "inactive"
  )

  if (modules.active) {
    orderedModules = modules.active.map((el, index) => ({
      ...el,
      order: index,
    }))
  }

  if (modules.inactive) {
    orderedModules = [
      ...orderedModules,
      ...modules.inactive.map((el, index) => ({
        ...el,
        order: index,
      })),
    ]
  }

  return orderedModules
}

const FormProject = ({ mode }) => {
  const { state, pathname } = useLocation()

  const {
    block,
    unblock,
    attemptedNavigation,
    setAttemptedNavigation,
    resumeBlockedNavigation,
    resetAttemptedNavigation,
  } = useNavigationBlock()

  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const [loading, setLoading] = useState(true)
  const { client: stateClient } = state || {}
  const { project: stateProject } = state || {}

  const [client, setClient] = useState(stateClient)
  const { setErrorMessage } = useToast()
  const isInt = SISU_CLIENTS.includes(stateClient?.clientId)
  const navigate = useNavigate()
  const [project, setProject] = useState({
    ...stateProject,
    modules:
      stateProject?.modules ||
      (isInt ?
        []
      : [
          {
            name: "No dev",
            id: "aux-id-0",
            isDeletable: 0,
            active: 1,
            order: 0,
          },
        ]),
  })
  const [error, setError] = useState({
    details: true,
  })

  const [originalProjectFormData, setOriginalProjectFormData] = useState({
    id: project?.id || null,
    clientId: stateClient?.id,
    name: project?.name || "",
    code: project?.code || "",
    active: project?.active ?? 1,
    previousMembersInCharge: project?.membersInCharge || null,
    membersInCharge: project?.membersInCharge || null,
    modules: project?.modules || [],
    isProjectDeletable: project?.isDeletable ?? 0,
  })

  const [formData, setFormData] = useState({ ...originalProjectFormData })

  const changedFormData = !areObjectsEqual(originalProjectFormData, {
    ...formData,
    modules: setInitialModuleOrder(
      formData.modules.map((el) => {
        delete el.selected
        return el
      })
    ),
    active: (() => {
      const active = formData.active
      if (typeof originalProjectFormData.active === "boolean") {
        if (typeof active === "number") return active === 1
        return active === true ? 1 : 0
      }
      if (typeof active === "number") return active
      return active === true ? 1 : 0
    })(),
  })

  const callSubmit = useRef(null)
  const hasErrors = error && Object.values(error).some((value) => value)

  const errorsRef = useRef(hasErrors)
  const madeAnEditRef = useRef(
    areObjectsEqual(formData, originalProjectFormData)
  )

  useEffect(() => {
    errorsRef.current = hasErrors
  }, [hasErrors])

  const onBeforeUnload = useCallback((e) => {
    if (!madeAnEditRef.current) return
    e.preventDefault()
    e.returnValue = false
  }, [])

  useEffect(() => {
    const madeAnEdit = !areObjectsEqual(formData, originalProjectFormData)

    if (madeAnEdit) {
      block()
    } else {
      unblock()
    }

    madeAnEditRef.current = madeAnEdit
  }, [formData, originalProjectFormData])

  const originalStateRef = useRef(state)

  useEffect(() => {
    originalStateRef.current = state
  }, [state])

  const blockedFormState = useRef(false)

  useEffect(() => {
    const changedFormData = !areObjectsEqual(formData, originalProjectFormData)

    if (changedFormData) {
      if (isFirefox) {
        if (!blockedFormState.current) {
          navigate(pathname, {
            replace: true,
            state: originalStateRef.current,
          })
          history.pushState(originalStateRef.current, "", location.href)
          blockedFormState.current = true
        }
      }
      block()
    } else {
      blockedFormState.current = false
      unblock()
    }

    madeAnEditRef.current = changedFormData
  }, [formData, originalProjectFormData])

  useEffect(() => {
    window.addEventListener("beforeunload", onBeforeUnload)
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload)
    }
  }, [])

  const hasBlockedHistoryStateAlready = history.state === null

  const onPopState = useShowConfirmationTextOnPopState({
    onCleanup: unblock,
    errorMessage: () =>
      errorsRef.current ?
        `¡Ups! Tus cambios dejaron errores\n\nSi sales ahora, se van a descartar. ¿Seguro que quieres continuar?`
      : `¡Espera! Tienes cambios sin guardar\n\nSi sales ahora, se pierden para siempre. ¿Seguro que quieres continuar?`,
    predicate: !areObjectsEqual(formData, originalProjectFormData),
  })

  const [validations, setValidations] = useState({
    name: {
      errors: [],
      value: project.name || "",
      typeValidations: [isNotEmpty],
    },
    code: {
      errors: [],
      value: project?.code || "",
      typeValidations: [isNotEmpty, isNotMaxLength],
      validationOptions: { isNotMaxLength: { max: 3 } },
    },
    active: {
      errors: [],
      value: project?.active || 1,
      typeValidations: [isNotEmpty],
    },
    membersInCharge: {
      errors: [],
      value: project?.membersInCharge || null,
      typeValidations: [],
    },
    modules: {
      errors: [],
      value: project?.modules || [],
      typeValidations: [],
    },
  })

  const isCreate = mode === FORM_CLIENT_MODE.CREATE

  const fetchData = async () => {
    setLoading(true)
    try {
      await Promise.all([gettingProject(), gettingClient()])
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: "Error al obtener los datos del proyecto",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!state) navigate(AppRoutes.clientes.index)
    if (mode === FORM_CLIENT_MODE.EDIT) {
      fetchData()
      checkAllFields(validations, setValidations)
    }
  }, [mode])

  const gettingProject = async () => {
    const responseProject = await getProjectData(stateProject.id)
    if (!responseProject.success)
      throw new Error("Error al obtener el proyecto")
    const responseModules = await getAllProjectModules(stateProject.id)
    if (!responseModules.success) throw new Error("Error al obtener el cliente")
    const project = {
      ...responseProject.result,
      modules: responseModules.result,
      clientId: stateClient?.id,
    }
    const newFormData = {
      id: project.projectID,
      clientId: project.clientID,
      name: project.projectName,
      code: project.projectTagName,
      active: project.active,
      previousMembersInCharge: project.membersInCharge || null,
      membersInCharge: project.membersInCharge || null,
      modules: setInitialModuleOrder(project.modules),
      isProjectDeletable: project.isProjectDeletable ?? 0,
    }

    setFormData(newFormData)
    setOriginalProjectFormData({ ...newFormData })

    setProject({
      ...responseProject.result,
      modules: responseModules.result,
      isProjectDeletable: project.isProjectDeletable ?? 0,
    })
    setValidations((prevState) => ({
      ...prevState,
      name: {
        ...prevState.name,
        value: project.name,
      },
      code: {
        ...prevState.code,
        value: project.code,
      },
      active: {
        ...prevState.active,
        value: project.active,
      },
      membersInCharge: {
        ...prevState.membersInCharge,
        value: project.membersInCharge,
      },
      modules: {
        ...prevState.modules,
        value: project.modules,
      },
    }))
  }
  const gettingClient = async () => {
    const response = await getClient(stateClient.id)
    if (!response.success) throw new Error("Error al obtener el cliente")
    const client = {
      ...response.result,
    }
    setClient(client)
  }
  const handleProjectDetailsChange = (e) => {
    const { name, value } = e.target
    const newValidations = validateFieldValue({ ...validations }, name, value)
    setFormData((prev) => ({ ...prev, [name]: value }))
    setValidations(newValidations)
    setError((prevError) => ({
      ...prevError,
      details: fieldsHaveErrors(newValidations),
    }))
  }

  if (!state) return null

  return (
    <div id="client-form" className={clsx(changedFormData && "fixed-footer")}>
      <Card className="form-card p-0 flex flex-column gap-3 p-1 shadow-none">
        <FormSectionTitle title="Información general" size="lg" />
        <FormProjectDetails
          client={client}
          formData={formData}
          handleChange={handleProjectDetailsChange}
          validations={validations}
          layout="horizontal"
          isCreate={isCreate}
        />
        <Divider className="my-3" stroke="sm" />
        <FormProjectModules
          client={client}
          project={formData}
          modules={formData.modules}
          error={error}
          setError={setError}
          setModules={(modules) => {
            handleProjectDetailsChange({
              target: { name: "modules", value: modules },
            })
          }}
          isCreate={isCreate}
        />
      </Card>

      <FormProjectFooter
        client={client}
        project={formData}
        error={error}
        isCreate={isCreate}
        validations={validations}
        loading={loading}
        saving={saving}
        deleting={deleting}
        setSaving={setSaving}
        setDeleting={setDeleting}
        callSubmit={callSubmit}
        changedFormData={changedFormData}
      />
      <ModalConfirmation
        closable
        content={{
          title: "¿Te quieres ir sin guardar?",
          message:
            hasErrors ?
              "A dónde vas tan rápido, tus cambios dejaron errores en tu formulario... ¿Qué quieres hacer con ellos?"
            : "Sabemos que vives al límite, pero antes... ¿Qué hacemos con tus cambios?",
          buttonCancelLabel: "Nah, bórralos",
          buttonSaveLabel: hasErrors ? "Volver y corregir" : "Guarda, porfa",
        }}
        onAccept={() => {
          if (hasErrors) {
            resetAttemptedNavigation()
          } else {
            callSubmit.current
              .handleSubmitProject()
              .then(() => {
                window.removeEventListener("popstate", onPopState)
                window.removeEventListener("beforeunload", onBeforeUnload)

                if (hasBlockedHistoryStateAlready) {
                  history.back()
                }
                unblock()
                setTimeout(
                  () => {
                    resumeBlockedNavigation()
                  },
                  isFirefox ? 100 : 0
                )
              })
              .catch(() => {
                resetAttemptedNavigation()
              })
          }
        }}
        onRequestCancel={() => {
          window.removeEventListener("popstate", onPopState)
          window.removeEventListener("beforeunload", onBeforeUnload)
          if (hasBlockedHistoryStateAlready) {
            history.back()
          }
          unblock()
          setTimeout(
            () => {
              resumeBlockedNavigation()
            },
            isFirefox ? 100 : 0
          )
        }}
        loading={saving || deleting}
        disabled={saving || deleting}
        visible={attemptedNavigation[0]}
        onHide={() => {
          resetAttemptedNavigation()
        }}
      />
    </div>
  )
}

export default FormProject
